import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";

const groups = [
  {
    tag: "Formulation Development",
    blurb: "From concept to a robust, scalable dosage form — engineered around your target profile.",
    items: ["Preformulation", "Formulation Design", "QbD & Nanotechnology", "Analytical Services"],
  },
  {
    tag: "Pharmaco-Scintigraphy",
    blurb: "Because we are reliable — see exactly where and when your drug acts, in vivo.",
    items: ["Human Scintigraphy", "Preclinical Scintigraphy", "Radiolabeling Validation", "Radiolabel Development", "Pharmacodynamic Studies"],
  },
  {
    tag: "Clinical Studies",
    blurb: "To redefine your brand with regulator-ready, ICH GCP-compliant evidence.",
    items: ["Proof-of-Concept", "Academic Trials", "Investigator Initiated Trials", "Phase 0, I & II Trials", "Pharmacokinetic Studies"],
  },
  {
    tag: "Product Evaluation",
    blurb: "Rigorous, quantitative testing that de-risks your entire development pathway.",
    items: ["Dissolution", "Dermatokinetics", "Skin Permeation", "Preclinical Safety & Efficacy"],
  },
];

const productDetails = [
  { t: "Parenteral Products", d: "Assessment of IV, IM, IP and SC formulations; biodistribution, pharmacokinetics and relative bioequivalence; proof-of-concept for depot formulations, implants and inserts; WBC scintigraphy for inflammation and disease sites; and targeting efficiency of systemic formulations." },
  { t: "Pulmonary Products", d: "Assessment and optimisation of nasal and pulmonary drug products; the impact of formulation, device and patient factors on lung deposition; head-to-head comparison of devices, spacers and products; and lung deposition patterns with mucociliary clearance." },
  { t: "Topical & Dermal", d: "Skin deposition, permeation and penetration including dermatokinetics; skin retention time and physical product attributes; skin penetration and dissolution of microarray patches; evaluation of transdermal patches; and follicular targeting with penetration enhancers." },
  { t: "Specialized & Herbal", d: "Herbal formulations, bioactives and nutraceuticals; nanoformulation in vivo behaviour, morphology and targeting; GI emptying studies of food; performance evaluation of cosmetics such as anti-ageing creams and lipsticks; and assessment of disinfectants and antiseptics." },
];

export default function Services() {
  return (
    <div className="bg-background">
      <section className="relative mx-auto max-w-[1400px] px-6 pb-14 pt-40 lg:px-10 lg:pt-48 grain">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Our services</p>
          <h1 className="mt-6 max-w-4xl font-serif-display text-5xl font-semibold leading-[0.98] tracking-tight text-foreground md:text-6xl lg:text-7xl">
            Expertise across the entire development lifecycle.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Every engagement is a tailored solution package — combining formulation craft, imaging
            science and clinical rigour to move your molecule forward with confidence.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-10">
        <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2">
          {groups.map((g, i) => (
            <Reveal key={g.tag} delay={i * 0.08} className="bg-card p-8 transition-colors duration-500 hover:bg-muted/40 lg:p-12">
              <h2 className="font-serif-display text-2xl font-semibold text-foreground md:text-3xl">{g.tag}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{g.blurb}</p>
              <ul className="mt-6 space-y-3">
                {g.items.map((it) => (
                  <li key={it} className="flex items-center gap-3 border-b border-border/70 pb-3 text-muted-foreground">
                    <span className="font-heading text-primary">→</span> {it}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-muted/40">
        <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">In depth</p>
            <h2 className="mt-4 font-serif-display text-3xl font-semibold text-foreground md:text-4xl">Product categories</h2>
          </Reveal>
          <div className="mt-12 space-y-px overflow-hidden rounded-2xl border border-border bg-border">
            {productDetails.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05} className="grid gap-4 bg-card p-8 md:grid-cols-[0.4fr_1fr] lg:p-10">
                <h3 className="font-serif-display text-xl font-semibold text-primary">{p.t}</h3>
                <p className="leading-relaxed text-muted-foreground">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
        <Reveal>
          <h2 className="max-w-3xl font-serif-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl">
            Have a specific study in mind?
          </h2>
          <Link to="/contact" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground shadow-luxe transition-colors duration-300 hover:bg-gold">
            Request a consultation
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
